import { SCHOLARSHIP_PLAYER_POSITIONS, type ScholarshipPlayerPosition } from '../../../../types/scholarships';

export type PositionGroup =
  | 'RHP'
  | 'LHP'
  | 'Catchers'
  | 'Infield'
  | 'Outfield'
  | 'DH/Utility'
  | 'Other';

export const POSITION_GROUPS: PositionGroup[] = [
  'RHP', 'LHP', 'Catchers', 'Infield', 'Outfield', 'DH/Utility', 'Other',
];

/**
 * Which sport's vocabulary to render in. Re-exported from `constants/sport` so the scholarships
 * call sites keep importing it from here, while the pitcher metrics can share the same rule
 * instead of writing a second copy of the fallback.
 */
export { type RosterSport, toRosterSport } from '../../../../constants/sport';
import type { RosterSport } from '../../../../constants/sport';

/**
 * The word a coach should see for a position. Display-only: the stored value is always the one
 * in SCHOLARSHIP_PLAYER_POSITIONS, so nothing here touches the wire format or the CHECK.
 *
 *   DH   -> 'DP' on a softball board; softball says designated player, baseball designated hitter.
 *   UTIL -> 'UTL' in both sports; that is what coaches write on their own boards.
 *
 * Every surface that shows a position -- dropdowns, dropdown-adjacent validation messages, the
 * budget editor, the players table's sub-headers AND its Position cells, the filter chips, the
 * CSV export, the depth chart cards -- routes through here. A raw position on screen is a bug:
 * it disagrees with whatever is next to it, which is how both relabellings were first missed.
 */
export const positionLabel = (
  position: ScholarshipPlayerPosition,
  sport: RosterSport,
): string => {
  if (position === 'UTIL') return 'UTL';
  if (position === 'DH' && sport === 'softball') return 'DP';
  return position;
};

/**
 * Filter values used by the sidebar checkboxes. Each individual position is
 * its own filter (so a coach can filter for just SS instead of all of Infield),
 * plus 'Other' to surface players with no assigned position.
 */
export type PositionFilter = ScholarshipPlayerPosition | 'Other';

/**
 * Ordering used in the sidebar — battery first, then infield, then outfield, then
 * 'Other'. Matches the table's group rendering order so the eye doesn't jump.
 */
export const POSITION_FILTERS: PositionFilter[] = [
  'RHP', 'LHP', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'UTIL', 'Other',
];

const POSITION_SET = new Set<string>(SCHOLARSHIP_PLAYER_POSITIONS);

export const isPositionFilter = (v: string): v is PositionFilter =>
  v === 'Other' || POSITION_SET.has(v);

/** Player → its filter value. Null positions map to 'Other'. */
export const positionToFilter = (
  position: ScholarshipPlayerPosition | null | undefined,
): PositionFilter => position ?? 'Other';

const GROUP_BY_POSITION: Record<ScholarshipPlayerPosition, PositionGroup> = {
  'RHP': 'RHP',
  'LHP': 'LHP',
  'C': 'Catchers',
  '1B': 'Infield',
  '2B': 'Infield',
  '3B': 'Infield',
  'SS': 'Infield',
  'LF': 'Outfield',
  'CF': 'Outfield',
  'RF': 'Outfield',
  // Their own group rather than 'Other'. 'Other' means "nobody has assigned this player a
  // position", and a designated hitter is the opposite of unassigned.
  'DH': 'DH/Utility',
  'UTIL': 'DH/Utility',
};

export function positionToGroup(
  position: ScholarshipPlayerPosition | null | undefined,
): PositionGroup {
  if (!position) return 'Other';
  return GROUP_BY_POSITION[position] ?? 'Other';
}

export function mapProspectPositionToEnum(
  prospectPosition: string | null | undefined,
  throws: string | null | undefined,
): ScholarshipPlayerPosition | null {
  const p = (prospectPosition ?? '').trim().toLowerCase();
  if (!p) return null;

  if (p === 'rhp') return 'RHP';
  if (p === 'lhp') return 'LHP';

  if (p === 'pitcher' || p === 'p') {
    return (throws ?? '').trim().toLowerCase().startsWith('l') ? 'LHP' : 'RHP';
  }
  if (p === 'catcher' || p === 'c') return 'C';
  if (p === 'first baseman' || p === '1b') return '1B';
  if (p === 'second baseman' || p === '2b') return '2B';
  if (p === 'third baseman' || p === '3b') return '3B';
  if (p === 'shortstop' || p === 'ss') return 'SS';
  if (p === 'left field' || p === 'left fielder' || p === 'lf') return 'LF';
  if (p === 'center field' || p === 'center fielder' || p === 'cf') return 'CF';
  if (p === 'right field' || p === 'right fielder' || p === 'rf') return 'RF';
  // Generic "outfield" / "OF" doesn't map cleanly to one of LF/CF/RF — let the
  // coach pick on the modal rather than silently bucketing to a default.
  return null;
}
